// ---------------------------------------------------------------------------
// The app shell's account layer.
//
// Two decisions here are worth the words:
//
//   1. ClerkProvider mounts only once /api/config has answered and only when a
//      publishable key came back. Until then the app renders without it, which
//      is also the permanent state of a deployment that has no key at all.
//   2. ClerkProvider is a SIBLING of the app rather than a wrapper around it.
//      The key arrives a moment after the first paint, and a provider that
//      wrapped the tree would change the element type above every screen at that
//      moment, unmounting and remounting whatever the player was already
//      looking at. Nothing in this app renders Clerk's components inline — the
//      sign-in card is opened imperatively and clerk-js portals it itself — so
//      the provider has no reason to be an ancestor of anything.
//
// The bridge inside it reads Clerk's session and this project's /api/me, and
// publishes both as one plain object. It deliberately never touches Clerk's user
// object: an email address and a Clerk user id are two of the things this app is
// never allowed to render, and the safest way to honour that is not to hold them.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ClerkProvider, useAuth, useClerk } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useSiteConfig } from '../../hooks/useSiteConfig.js';
import {
  ACCOUNT_UNKNOWN,
  AccountContext,
  GUEST_ONLY,
  type AccountApi,
} from '../../hooks/useAccount.js';
import { usePlayerStore } from '../../store/playerStore.js';
import { claimGuestGames, claimUsername, getMe, type Account } from '../../lib/api.js';
import { useClerkAppearance } from './clerkAppearance.js';
import { AccountOnboarding } from './AccountOnboarding.js';

function reasonFrom(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Could not reach the server.';
}

/** What /api/me answered, tagged with the session it was answering for. */
type Loaded =
  | { signedIn: false }
  | { signedIn: true; account: Account | null; error: string | null };

/** Apply a change to a held account, leaving anything else exactly as it was. */
function amend(held: Loaded | null, change: (account: Account) => Account): Loaded | null {
  if (held === null || !held.signedIn || held.account === null) return held;
  return { ...held, account: change(held.account) };
}

/**
 * Clerk is configured but has not answered yet. `ready` is false, so anything
 * that depends on knowing who this is waits instead of assuming a guest.
 */
const LOADING_ACCOUNT: AccountApi = { ...GUEST_ONLY, enabled: true, ready: false };

export function AccountProvider({ children }: { children: ReactNode }) {
  const { config, loading } = useSiteConfig();
  const [api, setApi] = useState<AccountApi | null>(null);

  const publishableKey =
    config?.clerkEnabled === true && config.clerkPublishableKey ? config.clerkPublishableKey : null;

  const value = loading
    ? ACCOUNT_UNKNOWN
    : publishableKey === null
      ? GUEST_ONLY
      : (api ?? LOADING_ACCOUNT);

  return (
    <>
      {publishableKey !== null && <ClerkGate publishableKey={publishableKey} publish={setApi} />}
      <AccountContext value={value}>
        {children}
        <AccountOnboarding />
      </AccountContext>
    </>
  );
}

function ClerkGate({
  publishableKey,
  publish,
}: {
  publishableKey: string;
  publish: (api: AccountApi) => void;
}) {
  const navigate = useNavigate();
  const appearance = useClerkAppearance();

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl="/"
      appearance={appearance}
      // Clerk's own navigations stay inside the router rather than reloading the
      // page, which would drop a room the player is sitting in.
      routerPush={(to: string) => navigate(to)}
      routerReplace={(to: string) => navigate(to, { replace: true })}
    >
      <ClerkBridge publish={publish} />
    </ClerkProvider>
  );
}

function ClerkBridge({ publish }: { publish: (api: AccountApi) => void }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const clerk = useClerk();
  const deviceId = usePlayerStore((store) => store.deviceId);

  /**
   * What /api/me last answered, and which session it answered for. Carrying the
   * signed-in flag with the answer is what makes staleness impossible to
   * misread: after a sign-out, or a sign-in as somebody else, the stored answer
   * no longer matches the session and `ready` goes false until a new one lands,
   * rather than the previous person's name sitting on screen for a moment.
   */
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  // Clerk's functions are reached through a ref rather than closed over, and
  // nothing below depends on their identity. The object this bridge publishes
  // goes into state one level up, so a dependency on a function that Clerk
  // happens to re-create each render is not a wasted render — it is a loop.
  const session = useRef({ getToken, signOut, openSignIn: () => clerk.openSignIn() });
  useEffect(() => {
    session.current = { getToken, signOut, openSignIn: () => clerk.openSignIn() };
  });

  const freshToken = useCallback(async () => (await session.current.getToken()) ?? undefined, []);

  useEffect(() => {
    if (!isLoaded) return;

    let live = true;

    void (async () => {
      if (!isSignedIn) {
        if (live) setLoaded({ signedIn: false });
        return;
      }

      try {
        const token = await session.current.getToken();
        if (!token) throw new Error('That session has expired. Sign in again.');

        const me = await getMe(token, deviceId);
        if (!live) return;
        setLoaded({
          signedIn: true,
          error: null,
          account: me.signedIn
            ? {
                username: me.username,
                displayName: me.displayName,
                createdAt: me.createdAt,
                claimableGames: me.claimableGames,
              }
            : null,
        });
      } catch (caught) {
        // A verified session whose account we cannot read is still a session.
        // It degrades to guest play with the server's reason attached, rather
        // than to a broken screen.
        if (live) setLoaded({ signedIn: true, account: null, error: reasonFrom(caught) });
      }
    })();

    return () => {
      live = false;
    };
  }, [isLoaded, isSignedIn, deviceId]);

  const signedIn = isLoaded && isSignedIn === true;
  const answered = loaded !== null && loaded.signedIn === signedIn;
  const account = answered && loaded.signedIn ? loaded.account : null;
  const error = answered && loaded.signedIn ? loaded.error : null;

  // An account carries a display name, and a player who has never typed one has
  // nothing to lose by taking it. A name they did type is never overwritten.
  useEffect(() => {
    const name = account?.displayName;
    if (!name) return;
    const store = usePlayerStore.getState();
    if (store.displayName.trim().length === 0) store.setDisplayName(name);
  }, [account?.displayName]);

  const chooseUsername = useCallback(async (username: string, displayName?: string) => {
    const token = await session.current.getToken();
    if (!token) throw new Error('That session has expired. Sign in again.');

    // 400, 409 and 429 all arrive as the server's own sentence, which is the
    // only place the username rules are stated to a player.
    const saved = await claimUsername(token, { username, displayName });
    setLoaded((previous) =>
      amend(previous, (held) => ({
        ...held,
        username: saved.username,
        displayName: saved.displayName,
      })),
    );
  }, []);

  const adoptGuestGames = useCallback(async () => {
    const token = await session.current.getToken();
    if (!token) throw new Error('That session has expired. Sign in again.');

    const result = await claimGuestGames(token, deviceId);
    setLoaded((previous) => amend(previous, (held) => ({ ...held, claimableGames: 0 })));
    return result.claimed;
  }, [deviceId]);

  const signIn = useCallback(() => {
    void session.current.openSignIn();
  }, []);

  const endSession = useCallback(() => {
    void session.current.signOut();
  }, []);

  const api = useMemo<AccountApi>(
    () => ({
      enabled: true,
      ready: isLoaded && answered,
      signedIn,
      account,
      error,
      signIn,
      signOut: endSession,
      chooseUsername,
      adoptGuestGames,
      freshToken,
    }),
    [
      isLoaded,
      answered,
      signedIn,
      account,
      error,
      signIn,
      endSession,
      chooseUsername,
      adoptGuestGames,
      freshToken,
    ],
  );

  useEffect(() => {
    publish(api);
  }, [api, publish]);

  return null;
}
